import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FaRobot, FaBrain, FaFileAlt, FaBell, FaCheck, FaTimes, FaMapMarkerAlt } from 'react-icons/fa';
import SectionHeader from './_primitives/SectionHeader';

/* ── Animated visual: Resume Optimisation ── */
const KEYWORDS = [
  { word: 'React',      pass: true  },
  { word: 'Node.js',    pass: true  },
  { word: 'Python',     pass: false },
  { word: 'Leadership', pass: true  },
  { word: 'SQL',        pass: false },
  { word: 'TypeScript', pass: true  },
];

function ResumeOptVisual() {
  return (
    <div className="h-28 rounded-xl border border-gray-100 bg-white px-4 py-3 flex gap-4 items-center overflow-hidden">
      {/* Score circle */}
      <div className="shrink-0 relative w-16 h-16">
        <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
          <circle cx="18" cy="18" r="15" fill="none" stroke="#F1F5F9" strokeWidth="3" />
          <motion.circle
            cx="18" cy="18" r="15" fill="none"
            stroke="#7C3AED" strokeWidth="3"
            strokeLinecap="round"
            strokeDasharray="94"
            initial={{ strokeDashoffset: 94 }}
            whileInView={{ strokeDashoffset: 12 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease: 'easeOut', delay: 0.3 }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <motion.span
            className="font-display font-black text-xs text-brand-purple"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.8 }}
          >
            87%
          </motion.span>
        </div>
      </div>

      {/* Keyword chips */}
      <div className="flex-1">
        <p className="text-[10px] font-black uppercase tracking-wide text-ink-300 mb-2">ATS Keywords</p>
        <div className="flex flex-wrap gap-1.5">
          {KEYWORDS.map((k, i) => (
            <motion.span
              key={k.word}
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 + i * 0.08 }}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold
                ${k.pass
                  ? 'bg-green-50 text-green-600 border border-green-200'
                  : 'bg-red-50 text-red-400 border border-red-200'}`}
            >
              {k.pass ? <FaCheck className="text-[8px]" /> : <FaTimes className="text-[8px]" />}
              {k.word}
            </motion.span>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── Animated visual: Smart Alerts ── */
const ALERTS = [
  { role: 'Frontend Developer',  company: 'Razorpay',    city: 'Bengaluru', delay: 0    },
  { role: 'Product Manager',     company: 'Flipkart',    city: 'Bengaluru', delay: 0.2  },
  { role: 'Data Analyst',        company: 'PhonePe',     city: 'Pune',      delay: 0.4  },
];

function SmartAlertsVisual() {
  return (
    <div className="h-28 rounded-xl border border-gray-100 bg-white px-3 py-2.5 space-y-1.5 overflow-hidden">
      {ALERTS.map((a, i) => (
        <motion.div
          key={a.role}
          initial={{ opacity: 0, x: 24 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ delay: a.delay, duration: 0.35 }}
          className="flex items-center justify-between px-3 py-1.5 rounded-lg bg-secondary-50 border border-secondary-100"
        >
          <div className="flex items-center gap-2">
            <span className="w-5 h-5 rounded-md bg-brand-purple/10 flex items-center justify-center shrink-0">
              <FaBell className="text-brand-purple text-[8px]" />
            </span>
            <div>
              <p className="text-[10px] font-black text-ink-900 leading-none">{a.role}</p>
              <p className="text-[9px] text-ink-400">{a.company}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="flex items-center gap-0.5 text-[9px] text-ink-400">
              <FaMapMarkerAlt className="text-[8px]" />{a.city}
            </span>
            <span className="px-1.5 py-0.5 rounded-full bg-brand-purple text-white text-[8px] font-black">
              New
            </span>
          </div>
        </motion.div>
      ))}
    </div>
  );
}

/* ── Feature data ── */
const FEATURES = [
  {
    icon:   <FaRobot />,
    title:  'AI Job Matching',
    desc:   'We rank jobs by fit — skills, location, experience, goals — not by recency.',
    img:    require('../../assets/ss/search.png'),
  },
  {
    icon:   <FaFileAlt />,
    title:  'AI Resume Builder',
    desc:   'Answer simple questions or upload your old resume — AI polishes it, checks it against real job posts, and gives you a clean PDF.',
    img:    require('../../assets/ss/resume.png'),
    href:   '/resume-builder',
  },
  {
    icon:   <FaBrain />,
    title:  'Resume Optimisation',
    desc:   'AI audits your resume against live job descriptions for higher recruiter visibility.',
    visual: <ResumeOptVisual />,
  },
  {
    icon:   <FaBell />,
    title:  'Smart Alerts',
    desc:   'Get notified the moment a matching role is posted — before 10,000 others apply.',
    visual: <SmartAlertsVisual />,
  },
];

export default function AIFeatures() {
  const navigate = useNavigate();
  return (
    <section className="py-14 md:py-24 px-4 sm:px-6 bg-white border-t border-gray-100">
      <div className="max-w-7xl mx-auto">
        <SectionHeader
          eyebrow="Powered by AI"
          title="Your career, on autopilot"
          subtitle="Four tools working behind the scenes so you can focus on getting hired."
        />
        <div className="grid md:grid-cols-2 gap-6">
          {FEATURES.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              onClick={f.href ? () => navigate(f.href) : undefined}
              className={`group flex flex-col sm:flex-row gap-4 sm:gap-6 items-start p-6 sm:p-8 rounded-3xl border border-gray-100
                         hover:border-brand-purple hover:shadow-purple
                         hover:-translate-y-1 transition-all duration-300 bg-paper ${f.href ? 'cursor-pointer' : ''}`}
            >
              <div className="w-14 h-14 shrink-0 rounded-2xl bg-secondary-50 border border-secondary-100
                              flex items-center justify-center text-xl text-brand-purple
                              group-hover:bg-brand-purple group-hover:text-white transition-all">
                {f.icon}
              </div>
              <div className="flex-1">
                <h3 className="font-display font-black text-xl text-ink-900 mb-2">{f.title}</h3>
                <p className="text-ink-500 text-sm leading-relaxed mb-4">{f.desc}</p>
                {f.img && (
                  <div className="h-28 rounded-xl overflow-hidden border border-gray-100 shadow-inner">
                    <img
                      src={f.img}
                      alt={f.title}
                      className="w-full h-full object-cover grayscale group-hover:grayscale-0
                                 transition-all duration-500"
                    />
                  </div>
                )}
                {f.visual && f.visual}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
